import '../css/tailwind.css';

import React from 'react';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import { ThemeProvider } from 'styled-components';
import {
  ApiSite,
  ContentTypeAPI,
  defaultResponseHandler,
  formatMs,
  keycloakEntry,
  Loading,
  MssGlobalStyles,
  theme,
  UserProvider,
  useUserinfo,
} from '@mss/mss-component-library';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import {
  AllPermissionAPI,
  perm,
  PermissionSetOption,
} from './authorization/util/permissionsForEdit';
import { flattenDeep } from 'lodash';
import pjson from '../../package.json';
import i18n from 'i18next';
import Backend from 'i18next-http-backend';
import { initReactI18next, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import Fetch from './common/util/_fetch';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  useAllMonitorsQuery,
  useLicensesQuery,
} from './asset/services/queries';

export type ConfigJsonSchemaProperty = {
  items?: { type: 'string' | 'number' };
  maxItems?: number;
  minItems?: number;
  title: string;
  type: 'object' | 'array' | 'string' | 'number' | 'boolean';
};

export type ConfigJsonSchemaProperties = Record<
  string,
  ConfigJsonSchemaProperty
>;

export type ConfigJsonSchema = {
  description: string;
  properties: ConfigJsonSchemaProperties;
  required: (keyof ConfigJsonSchemaProperties)[];
  title: string;
  type: 'object';
};

export type AllMonitorsAPI = {
  name: string;
  category: string;
  config_json_schema: ConfigJsonSchema;
  supported_series?: number[];
};

export type AllMonitors = Omit<AllMonitorsAPI, 'config_json_schema'> & {
  config_json_schema: ConfigJsonSchema | null;
};

export type LicenseEntity = {
  id: number;
  expires_at: string;
  name: string;
};

type MSSVersions = {
  mss_site_api: string;
  mss_site_api_tag: string;
  mss_site_api_full: string;
  mss_site_client: string;
  mss_component_library: string;
  celery: string;
  django: string;
  react: string;
  redis: string;
};

export type IAppContext = {
  contentTypes: ContentTypeAPI[];
  allPermissionsMap: Map<string, number>;
  invertedAllPermissionsMap: Map<number, string>;
  permissionsWithView: PermissionSetOption[];
  allMonitors: AllMonitors[];
  siteName: string;
  customerLogo: string;
  mssVersions: MSSVersions;
  allLicenses: LicenseEntity[];
};

const initialAppContext = {
  contentTypes: [],
  allPermissionsMap: new Map<string, number>(),
  invertedAllPermissionsMap: new Map<number, string>(),
  permissionsWithView: [],
  allMonitors: [],
  siteName: '',
  customerLogo: '',
  mssVersions: {} as any,
  allLicenses: [],
};

export const AppContext = React.createContext<IAppContext>(initialAppContext);

type LicenseArgs = {
  limit: number | null;
  total?: number;
};

export type License = {
  [key: string]: LicenseArgs;
};

const editPermissions = [
  'add_grouppermission',
  'change_grouppermission',
  'delete_grouppermission',
  'view_grouppermission',
  'add_userpermission',
  'change_userpermission',
  'delete_userpermission',
  'view_userpermission',
  'add_groupobjectpermission',
  'change_groupobjectpermission',
  'delete_groupobjectpermission',
  'view_groupobjectpermission',
  'add_userobjectpermission',
  'change_userobjectpermission',
  'delete_userobjectpermission',
  'view_userobjectpermission',
];

const defaultPerms = perm(() => null).map(perm =>
  perm.permissions.map(perm2 => perm2.options)
);
export const permissionsWithView: PermissionSetOption[] = flattenDeep(
  defaultPerms as any
);

export function createPermissionMaps(arr: AllPermissionAPI[]) {
  return arr.reduce(
    (
      acc: {
        idByCodename: Map<string, number>;
        codenameById: Map<number, string>;
      },
      perm: AllPermissionAPI
    ) => {
      const alreadyExists = acc.idByCodename.has(perm.codename);

      if (!alreadyExists) {
        acc.idByCodename.set(perm.codename, perm.id);
        acc.codenameById.set(perm.id, perm.codename);
      } else {
        import.meta.env.DEV &&
          import.meta.env.VITE_DEBUG_PERMISSIONS &&
          console.info('Duplicate codename!', perm);
      }

      return acc;
    },
    {
      idByCodename: new Map<string, number>(),
      codenameById: new Map<number, string>(),
    }
  );
}

const getAndMapPermissions = (t: TFunction) => async () => {
  const { data, error } = await defaultResponseHandler<AllPermissionAPI[]>({
    request: Fetch.get('/api/permission/'),
    t,
  });
  if (error) return null;
  return createPermissionMaps(data);
};

const getAndMapSiteConfiguration = (t: TFunction) => async () => {
  const { data, error } = await defaultResponseHandler<
    ApiSite & {
      versions: MSSVersions;
      license_data: { licenses: LicenseEntity[] };
    }
  >({
    request: Fetch.get('/api/configuration/'),
    t,
  });
  if (error) return undefined;
  return {
    ...data,
    mssVersions: {
      ...data.versions,
      mss_site_client: pjson.version,
      mss_component_library: pjson.dependencies['@mss/mss-component-library'],
      mss_site_api_full: data.versions.mss_site_api,
      mss_site_api: data.versions.mss_site_api?.split('-')[0] || '',
      mss_site_api_tag: data.versions.mss_site_api?.split('-')[1] || '',
    },
  };
};

export const useConfigurationQuery = () => {
  const { t } = useTranslation();
  return useQuery({
    queryKey: ['configuration'],
    cacheTime: Infinity,
    staleTime: Infinity,
    queryFn: getAndMapSiteConfiguration(t),
  });
};

const Bootstrap = () => {
  const [hasPermission] = useUserinfo();
  const canEditPermissions = hasPermission(editPermissions);

  const { t } = useTranslation();
  const { data: permissions } = useQuery({
    queryKey: ['permissions'],
    cacheTime: Infinity,
    staleTime: Infinity,
    refetchInterval: formatMs({ hours: 1 }),
    enabled: canEditPermissions,
    queryFn: getAndMapPermissions(t),
  });

  const { data: allMonitors } = useAllMonitorsQuery();

  const { data: contentTypes = canEditPermissions ? null : [] } = useQuery({
    queryKey: ['content-type'],
    cacheTime: Infinity,
    staleTime: Infinity,
    enabled: canEditPermissions,
    queryFn: async () =>
      (
        await defaultResponseHandler<ContentTypeAPI[]>({
          request: Fetch.get('/api/content-type/'),
          t,
        })
      ).data ?? null,
  });

  useLicensesQuery();

  const { data: siteData } = useConfigurationQuery();

  const canBootstrap =
    !!contentTypes &&
    (!!permissions || !canEditPermissions) &&
    !!allMonitors &&
    !!siteData;

  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <AppContext.Provider
          value={{
            contentTypes: contentTypes ?? initialAppContext.contentTypes,
            allPermissionsMap:
              permissions?.idByCodename ?? initialAppContext.allPermissionsMap,
            invertedAllPermissionsMap:
              permissions?.codenameById ??
              initialAppContext.invertedAllPermissionsMap,
            permissionsWithView,
            allMonitors: allMonitors ?? initialAppContext.allMonitors,
            siteName: siteData?.name ?? initialAppContext.siteName,
            mssVersions: siteData?.mssVersions ?? initialAppContext.mssVersions,
            customerLogo: siteData?.logo ?? initialAppContext.customerLogo,
            allLicenses:
              siteData?.license_data?.licenses ?? initialAppContext.allLicenses,
          }}
        >
          <MssGlobalStyles theme={theme} />
          {!canBootstrap && <Loading />}
          {canBootstrap && <App />}
        </AppContext.Provider>
      </ThemeProvider>
    </BrowserRouter>
  );
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: formatMs({ seconds: 5 }),
      cacheTime: formatMs({ minutes: 1 }),
      refetchIntervalInBackground: false,
      refetchInterval: formatMs({ minutes: 1 }),
      initialDataUpdatedAt: 1,
      refetchOnMount: 'always',
      retry: import.meta.env.PROD ? 3 : false,
    },
  },
});

const Root = ({ keycloak }: any) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      <UserProvider
        keycloak={keycloak}
        variant="site"
        onFail={({ data }, setAccessDenied) => {
          if (String(data?.detail).startsWith('User account is disabled')) {
            setAccessDenied?.(true);
          } else {
            window.location.reload();
          }
        }}
      >
        <Bootstrap />
      </UserProvider>
    </QueryClientProvider>
  );
};

export type Tokens = {
  access?: string;
  refresh?: string;
  oneTimeToken?: string;
};

const url = `${import.meta.env.VITE_API_URL || '/api'}/auth/config/`;
const tokens = {
  access: sessionStorage.getItem('token') || undefined,
  refresh: sessionStorage.getItem('refresh-token') || undefined,
} as Tokens;
const oneTimeToken = new URLSearchParams(
  window.location.href.slice(window.location.href.indexOf('?'))
).get('token');
if (oneTimeToken) {
  console.info('Authenticating with one-time-token.');
  tokens.oneTimeToken = oneTimeToken;
}

const debug =
  !!import.meta.env.VITE_DEBUG_I18N && import.meta.env.VITE_DEBUG_I18N !== '0';
i18n
  .use(Backend)
  .use(initReactI18next)
  .init({
    react: { useSuspense: false },
    fallbackLng: 'en',
    fallbackNS: ['common', 'asset'],
    debug,
    keySeparator: false,
    returnNull: false, // React 18 compatibility: ensures t() always returns string, not null
    backend: {
      loadPath: `${import.meta.env.VITE_PUBLIC_URL ?? ''}/locales/{{lng}}/{{ns}}.json`,
    },
  })
  .then(async () => {
    await keycloakEntry(url, tokens, Root);
  });
